import { useCallback, useState } from "react";
import { AuditoriaService } from "../services/auditoriaService";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type {
  OrganizationByEmailResponse,
  ClientByEmailResponse,
  VerificationKycLinkStatus,
} from "../types/auditoria-api.types";

export interface VerificationLink {
  verification_url: string;
  status: VerificationKycLinkStatus;
  created_at: string;
  type: "organization" | "client";
  name: string;
}

export interface UseVerificationRequestsSectionReturn {
  organizationEmail: string;
  setOrganizationEmail: (value: string) => void;
  clientEmail: string;
  setClientEmail: (value: string) => void;
  verificationLinks: VerificationLink[];
  isLoading: boolean;
  error: string | null;
  noAccess: boolean;
  hasSearched: boolean;
  handleSearch: () => void;
  handleReset: () => void;
}

export function useVerificationRequestsSection(): UseVerificationRequestsSectionReturn {
  const [organizationEmail, setOrganizationEmail] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [verificationLinks, setVerificationLinks] = useState<VerificationLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noAccess, setNoAccess] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    const orgTrimmed = organizationEmail.trim();
    const clientTrimmed = clientEmail.trim();

    if (!orgTrimmed && !clientTrimmed) return;

    setIsLoading(true);
    setError(null);
    setNoAccess(false);
    setVerificationLinks([]);
    setHasSearched(true);

    const links: VerificationLink[] = [];

    try {
      const promises: Promise<void>[] = [];

      if (orgTrimmed) {
        promises.push(
          AuditoriaService.fetchOrganizationByEmail(orgTrimmed).then((res) => {
            const data: OrganizationByEmailResponse = res.data;
            data.kyc_links.forEach((link) => {
              links.push({
                verification_url: link.verification_url,
                status: link.status,
                created_at: link.created_at,
                type: "organization",
                name: data.organization.name || data.organization.email,
              });
            });
          })
        );
      }

      if (clientTrimmed) {
        promises.push(
          AuditoriaService.fetchClientByEmail(clientTrimmed).then((res) => {
            const data: ClientByEmailResponse = res.data;
            const clientName =
              [data.client.first_name, data.client.last_name].filter(Boolean).join(" ").trim() ||
              data.client.company_name ||
              data.client.email ||
              data.client.id;
            data.kyc_links.forEach((link) => {
              links.push({
                verification_url: link.verification_url,
                status: link.status,
                created_at: link.created_at,
                type: "client",
                name: clientName,
              });
            });
          })
        );
      }

      await Promise.all(promises);
      setVerificationLinks(links);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setNoAccess(true);
        return;
      }
      if (HttpApiService.isNotFoundError(err)) {
        const searchingOrg = !!orgTrimmed;
        setError(searchingOrg ? "NOT_FOUND_ORG" : "NOT_FOUND_CLIENT");
        return;
      }
      setError(HttpApiService.getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [organizationEmail, clientEmail]);

  const handleReset = useCallback(() => {
    setOrganizationEmail("");
    setClientEmail("");
    setVerificationLinks([]);
    setError(null);
    setNoAccess(false);
    setHasSearched(false);
  }, []);

  return {
    organizationEmail,
    setOrganizationEmail,
    clientEmail,
    setClientEmail,
    verificationLinks,
    isLoading,
    error,
    noAccess,
    hasSearched,
    handleSearch,
    handleReset,
  };
}
